import React, { useState, useEffect, createContext, useContext } from 'react';
import userApi from '../../api/userApi';
import { useAuth } from '../userManagement/AuthContext';

/**
 * Authorization context to share permission checks across the application
 */
const AuthorizationContext = createContext({
  permissions: [],
  hasPermission: () => false,
  hasAnyPermission: () => false,
  hasAllPermissions: () => false,
  isLoading: true,
  error: null,
  refreshPermissions: () => {},
});

/**
 * Authorization provider component
 * Now uses permissions from login response (stored in localStorage) instead of separate API
 */
export const AuthorizationProvider = ({ children }) => {
  const { user, isAuthenticated, userPermissions } = useAuth();
  const [permissions, setPermissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load user permissions from AuthContext or localStorage
  useEffect(() => {
    if (isAuthenticated) {
      loadPermissions();
    } else {
      setPermissions([]);
      setIsLoading(false);
    }
  }, [isAuthenticated, user?.id, userPermissions]);

  // Load permissions from AuthContext (which gets them from login response)
  const loadPermissions = () => {
    setIsLoading(true);
    setError(null);

    try {
      // First try to get permissions from AuthContext
      let perms = userPermissions;
      
      // Fallback to localStorage if not in context
      if (!perms || perms.length === 0) {
        try {
          const stored = localStorage.getItem('userPermissions');
          perms = stored ? JSON.parse(stored) : [];
        } catch (e) {
          console.error('Error parsing stored permissions:', e);
          perms = [];
        }
      }
      
      console.log('Loaded permissions from login:', perms);
      
      // Convert string array to permission objects for compatibility
      if (perms && Array.isArray(perms)) {
        const permObjects = perms.map(p => 
          typeof p === 'string' ? { name: p, granted: true } : p
        );
        setPermissions(permObjects);
      }
    } catch (err) {
      console.error('Error loading permissions:', err);
      setError(err.message || 'Failed to load permissions');
    } finally {
      setIsLoading(false);
    }
  };

  // Check if user has a specific permission
  const hasPermission = (permissionName) => {
    if (!isAuthenticated) return false;
    if (user?.is_admin) return true; // Admin has all permissions
    
    // Check if the permission exists in our permission array (from login)
    const permission = permissions.find(p => p.name === permissionName);
    if (permission && permission.granted) {
      return true;
    }
    
    // Also check direct string match (new format from tag permissions)
    const hasDirectPermission = permissions.some(p => 
      (typeof p === 'string' && p === permissionName) ||
      (p.name === permissionName && p.granted)
    );
    
    if (hasDirectPermission) {
      return true;
    }
    
    return false;
  };

  // Check if user has any of the specified permissions
  const hasAnyPermission = (permissionNames) => {
    if (!isAuthenticated) return false;
    if (user?.is_admin) return true; // Admin has all permissions
    
    console.log(`Checking any permissions: ${JSON.stringify(permissionNames)} for user with roles: ${JSON.stringify(user?.roles)}`);
    
    const result = permissionNames.some(name => hasPermission(name));
    console.log(`Result of hasAnyPermission check: ${result}`);
    return result;
  };

  // Check if user has all of the specified permissions
  const hasAllPermissions = (permissionNames) => {
    if (!isAuthenticated) return false;
    if (user?.is_admin) return true; // Admin has all permissions
    
    return permissionNames.every(name => hasPermission(name));
  };

  // Context value
  const value = {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isLoading,
    error,
    refreshPermissions: loadPermissions,
  };

  return (
    <AuthorizationContext.Provider value={value}>
      {children}
    </AuthorizationContext.Provider>
  );
};

/**
 * Hook to use the authorization context
 */
export const useAuthorization = () => {
  const context = useContext(AuthorizationContext);
  if (!context) {
    throw new Error('useAuthorization must be used within an AuthorizationProvider');
  }
  return context;
};

/**
 * Component that conditionally renders children based on permission
 */
export const PermissionGuard = ({ 
  permission, 
  permissions, 
  requireAll = false, 
  fallback = null,
  children 
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = useAuthorization();
  
  let hasAccess = false;
  
  if (permission) {
    // Single permission check
    hasAccess = hasPermission(permission);
  } else if (permissions) {
    // Multiple permissions check
    hasAccess = requireAll 
      ? hasAllPermissions(permissions) 
      : hasAnyPermission(permissions);
  }
  
  // Render children if has access, otherwise render fallback
  return hasAccess ? children : fallback;
};

export default { AuthorizationProvider, useAuthorization, PermissionGuard };
